import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { H2, Text } from '../components/ui/Typography';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import SafeIcon from '../common/SafeIcon';

const InternationalScan = () => {
  const { lang = 'nl' } = useParams();
  const isEn = lang === 'en';
  const navigate = useNavigate();
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState({});
  const [analyzing, setAnalyzing] = useState(false);

  const questions = [
    {
      id: 'nationality',
      q: isEn ? 'Do you have multiple nationalities?' : 'Heb je meerdere nationaliteiten?',
      opts: isEn 
        ? ['No, only Dutch nationality', 'Yes, Dutch and another nationality', 'I am an expat (no Dutch nationality)']
        : ['Nee, alleen de Nederlandse', 'Ja, Nederlandse en een andere', 'Ik ben een expat (geen NL nationaliteit)']
    },
    {
      id: 'residency',
      q: isEn ? 'What is your current country of residence?' : 'Wat is je huidige woonland?',
      opts: isEn 
        ? ['Netherlands', 'Within the EU', 'Outside the EU'] 
        : ['Nederland', 'Binnen de EU', 'Buiten de EU']
    },
    {
      id: 'property',
      q: isEn ? 'Do you own real estate abroad?' : 'Bezit je onroerend goed in het buitenland?',
      opts: isEn 
        ? ['No', 'Yes, in the EU', 'Yes, outside the EU'] 
        : ['Nee', 'Ja, in de EU', 'Ja, buiten de EU']
    },
    {
      id: 'assets',
      q: isEn ? 'Do you have bank accounts or investments in multiple countries?' : 'Heb je bankrekeningen of beleggingen in diverse landen?',
      opts: isEn 
        ? ['No', 'Yes, in 1 other country', 'Yes, in multiple countries'] 
        : ['Nee', 'Ja, in 1 ander land', 'Ja, in meerdere landen']
    },
    {
      id: 'heirs',
      q: isEn ? 'Do your intended heirs live in the Netherlands?' : 'Wonen je beoogde erfgenamen in Nederland?',
      opts: isEn 
        ? ['Yes, all of them', 'Some live abroad', 'None of them live in NL']
        : ['Ja, allemaal', 'Sommigen wonen in het buitenland', 'Geen van hen woont in NL']
    }
  ];

  const handleStart = () => setStep(0);
  
  const handleAnswer = (ans) => {
    const currentQ = questions[step];
    setAnswers({ ...answers, [currentQ.id]: ans });
    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      setStep(step + 1);
      setAnalyzing(true);
      setTimeout(() => setAnalyzing(false), 2500);
    }
  };

  const goToResult = () => {
    navigate(`/${lang}/testament-scan/resultaat`, { state: { answers, type: 'international' } });
  };

  const renderContent = () => {
    if (step === -1) {
      return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-6">
          <div className="w-20 h-20 bg-navy-50 rounded-[2rem] flex items-center justify-center mx-auto mb-8 border border-navy-100">
            <SafeIcon name="Globe" className="text-3xl text-navy-900" />
          </div>
          <H2 className="mb-4 text-navy-900">{isEn ? 'International Intake' : 'Internationale Intake'}</H2>
          <Text className="mb-10 max-w-sm mx-auto text-slate-500 text-sm">
            {isEn 
              ? 'Map out your cross-border situation. We analyze the impact of the EU Succession Regulation on your assets.'
              : 'Breng je grensoverschrijdende situatie in kaart. Wij analyseren de impact van de EU Erfrechtverordening op jouw vermogen.'}
          </Text>
          <Button onClick={handleStart} className="w-full py-4 text-base">
            {isEn ? 'Start the intake' : 'Start de intake'}
          </Button>
        </motion.div>
      );
    }

    if (step < questions.length) {
      const q = questions[step];
      return (
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="py-4">
          <div className="flex justify-between items-center mb-10">
            <div className="text-[10px] font-bold text-navy-500 uppercase tracking-widest flex items-center gap-2">
              <SafeIcon name="Globe" /> {isEn ? 'Expertise Scan' : 'Expertise Scan'}
            </div>
            <div className="text-[10px] font-bold text-slate-400">
              {isEn ? `STEP ${step + 1} / ${questions.length}` : `STAP ${step + 1} / ${questions.length}`}
            </div>
          </div>
          <h3 className="text-2xl font-bold text-navy-900 mb-8 leading-tight">{q.q}</h3>
          <div className="space-y-3">
            {q.opts.map(opt => (
              <button
                key={opt}
                onClick={() => handleAnswer(opt)}
                className="w-full text-left px-6 py-5 rounded-2xl border border-slate-100 bg-slate-50/50 hover:bg-white hover:border-navy-900/20 hover:shadow-premium transition-all font-semibold text-navy-900 group"
              >
                <div className="flex justify-between items-center">
                  {opt}
                  <SafeIcon name="ArrowRight" className="text-slate-300 group-hover:text-navy-900 transition-colors" />
                </div>
              </button>
            ))}
          </div>
        </motion.div>
      );
    }

    if (analyzing) {
      return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20 flex flex-col items-center">
          <div className="relative w-24 h-24 mb-10">
            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }} className="absolute inset-0 border-4 border-slate-100 border-t-navy-900 rounded-full" />
            <div className="absolute inset-0 flex items-center justify-center">
              <SafeIcon name="Globe" className="text-2xl text-navy-900" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-navy-900">{isEn ? 'Strategic Analysis...' : 'Strategische Analyse...'}</h3>
          <p className="text-slate-500 mt-3 text-sm max-w-xs leading-relaxed">
            {isEn 
              ? 'We test your situation against the EU Succession Regulation and international tax treaties between the Netherlands and other countries.'
              : 'We toetsen je situatie aan de EU Erfrechtverordening en internationale fiscale verdragen tussen Nederland en overige landen.'}
          </p>
        </motion.div>
      );
    }

    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="py-6">
        <div className="flex items-center gap-5 mb-10 p-6 bg-navy-950 text-white rounded-[2rem] border border-navy-800 shadow-2xl">
          <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 border border-white/10">
            <SafeIcon name="Check" className="text-2xl" />
          </div>
          <div>
            <h3 className="text-lg font-bold">{isEn ? 'Analysis Ready' : 'Analyse Gereed'}</h3>
            <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mt-1">
              {isEn ? 'Status: Validated Profile' : 'Status: Gevalideerd Profiel'}
            </p>
          </div>
        </div>
        <Button onClick={goToResult} className="w-full py-5 text-base shadow-2xl shadow-navy-900/20" variant="primary">
          {isEn ? 'View international report' : 'Bekijk internationaal rapport'}
        </Button>
      </motion.div>
    );
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-24 px-6 bg-white relative overflow-hidden">
      <div className="absolute inset-0 bg-blueprint-pattern opacity-[0.03] pointer-events-none"></div>
      <Card className="max-w-xl w-full p-10 md:p-14 relative z-10 overflow-visible border-slate-100 shadow-2xl" hover={false}>
        <AnimatePresence mode="wait">
          {renderContent()}
        </AnimatePresence>
      </Card>
    </div>
  );
};

export default InternationalScan;